import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  History,
  Filter,
  Search,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  Eye,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Clock,
} from "lucide-react";

// ── Helper: get marks awarded from attempt DB record ──────────────────────────
// DB stores: evaluation.marks_awarded (NOT marks_awarded_total, NOT top-level score_awarded)
// DB stores: question_snapshot.marks_total (NOT evaluation.marks_total, NOT top-level total_marks)
const getMarksAwarded = (attempt) =>
  attempt?.evaluation?.marks_awarded ??
  attempt?.evaluation?.marks_awarded_total ??
  attempt?.score_awarded ??
  null;

const getMarksTotal = (attempt) =>
  attempt?.question_snapshot?.marks_total ??
  attempt?.evaluation?.marks_total ??
  attempt?.total_marks ??
  null;

// ── FeedbackPanel: handles both new personalised_feedback dict and old feedback formats ──
const FeedbackPanel = ({ attempt }) => {
  const fb =
    attempt?.evaluation?.personalised_feedback ||
    attempt?.evaluation?.feedback;

  if (!fb) return null;

  if (typeof fb === "string") {
    return (
      <div>
        <p className="text-xs text-slate-500 mb-2">AI Feedback</p>
        <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
          <p className="text-sm text-blue-800">{fb}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-slate-500 mb-2">AI Feedback</p>
      <div className="space-y-3">
        {fb.summary && (
          <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
            <p className="text-sm text-blue-800">{fb.summary}</p>
          </div>
        )}
        {fb.strengths?.length > 0 && (
          <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
            <p className="text-xs font-semibold text-emerald-700 mb-2">✓ What you got right</p>
            <ul className="space-y-1">
              {fb.strengths.map((s, i) => (
                <li key={i} className="text-xs text-emerald-700">• {s}</li>
              ))}
            </ul>
          </div>
        )}
        {fb.gaps?.length > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl">
            <p className="text-xs font-semibold text-amber-700 mb-2">✗ What was missing</p>
            <ul className="space-y-1">
              {fb.gaps.map((g, i) => (
                <li key={i} className="text-xs text-amber-700">• {g}</li>
              ))}
            </ul>
          </div>
        )}
        {fb.examiner_tip && (
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
            <p className="text-xs font-semibold text-slate-600 mb-1">💡 Examiner Tip</p>
            <p className="text-xs text-slate-600">{fb.examiner_tip}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const AttemptsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSubject, setFilterSubject] = useState("all");
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchAttempts();
  }, [filterSubject]);

  const fetchAttempts = async () => {
    try {
      let url = `${API}/attempts?limit=50`;
      if (filterSubject !== "all") {
        url += `&subject=${filterSubject}`;
      }
      const response = await axios.get(url);
      setAttempts(response.data);
    } catch (error) {
      console.error("Error fetching attempts:", error);
      toast.error("Failed to load attempts");
    } finally {
      setLoading(false);
    }
  };

  const viewDetail = (attempt) => {
    setSelectedAttempt(attempt);
    setDetailOpen(true);
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 70) return "text-emerald-600";
    if (percentage >= 50) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreBg = (percentage) => {
    if (percentage >= 70) return "bg-emerald-100";
    if (percentage >= 50) return "bg-amber-100";
    return "bg-red-100";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#2E3A59] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#2E3A59] font-medium">Loading attempts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation role="student" />
      
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="mb-2 -ml-2"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl lg:text-3xl font-heading font-bold text-[#2E3A59]">
                My Attempts
              </h1>
              <p className="text-slate-500 mt-1">
                Review your practice history and performance
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#2E3A59]"
            >
              <option value="all">All Subjects</option>
              <option value="Anatomy">Anatomy</option>
              <option value="Physiology">Physiology</option>
              <option value="Biochemistry">Biochemistry</option>
            </select>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <p className="text-2xl font-bold text-[#2E3A59]">{attempts.length}</p>
              <p className="text-xs text-slate-500">Total Attempts</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <p className="text-2xl font-bold text-emerald-600">
                {attempts.filter(a => (a.evaluation?.percentage || 0) >= 70).length}
              </p>
              <p className="text-xs text-slate-500">High Score (70%+)</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <p className="text-2xl font-bold text-amber-600">
                {attempts.filter(a => {
                  const pct = a.evaluation?.percentage || 0;
                  return pct >= 50 && pct < 70;
                }).length}
              </p>
              <p className="text-xs text-slate-500">Medium (50-70%)</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <p className="text-2xl font-bold text-red-600">
                {attempts.filter(a => (a.evaluation?.percentage || 0) < 50).length}
              </p>
              <p className="text-xs text-slate-500">Needs Work (&lt;50%)</p>
            </div>
          </div>

          {/* Attempts List */}
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            {attempts.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {attempts.map((attempt, i) => {
                  const percentage = attempt.evaluation?.percentage || 0;
                  const awarded = getMarksAwarded(attempt);
                  const total = getMarksTotal(attempt);
                  
                  return (
                    <div
                      key={attempt.attempt_id || i}
                      className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => viewDetail(attempt)}
                    >
                      <div className="flex items-start gap-4">
                        {/* Score Badge */}
                        <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center ${getScoreBg(percentage)}`}>
                          <span className={`text-lg font-bold ${getScoreColor(percentage)}`}>
                            {percentage}%
                          </span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                              {attempt.subject}
                            </span>
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                              {attempt.question_snapshot?.question_type || attempt.question_type || "Essay"}
                            </span>
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(attempt.student_answer?.submitted_at || attempt.submitted_at)}
                            </span>
                          </div>
                          
                          <p className="text-sm text-[#2E3A59] font-medium line-clamp-2">
                            {attempt.question_snapshot?.question_text || attempt.question_text || "Question"}
                          </p>
                          
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-slate-500">
                              Score: {awarded !== null ? awarded : "—"}/{total !== null ? total : "—"}
                            </span>
                            {attempt.student_answer?.time_taken_seconds && (
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {Math.round(attempt.student_answer.time_taken_seconds / 60)} min
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Arrow */}
                        <Eye className="w-5 h-5 text-slate-400 shrink-0" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <History className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="font-semibold text-[#2E3A59] mb-2">No attempts yet</h3>
                <p className="text-sm text-slate-400 mb-4">Start practicing to see your attempts here</p>
                <Button onClick={() => navigate("/dashboard")} className="bg-[#2E3A59]">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Start Practice
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Attempt Details</DialogTitle>
          </DialogHeader>
          
          {selectedAttempt && (() => {
            const awarded = getMarksAwarded(selectedAttempt);
            const total   = getMarksTotal(selectedAttempt);
            const pct     = selectedAttempt.evaluation?.percentage ||
                            (awarded !== null && total ? Math.round((awarded / total) * 100) : 0);

            return (
              <div className="py-4 space-y-4">
                {/* Score Header */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Score</p>
                    <p className="text-3xl font-bold text-[#2E3A59]">
                      {awarded !== null ? awarded : "—"}/{total !== null ? total : "—"}
                    </p>
                  </div>
                  <div className={`text-4xl font-bold ${getScoreColor(pct)}`}>
                    {pct}%
                  </div>
                </div>

                {/* Question */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-xs text-slate-500 mb-2">Question</p>
                  <p className="text-[#2E3A59] font-medium question-text">
                    {selectedAttempt.question_snapshot?.question_text || selectedAttempt.question_text}
                  </p>
                </div>

                {/* Your Answer */}
                <div>
                  <p className="text-xs text-slate-500 mb-2">Your Answer</p>
                  <div className="p-4 bg-white border border-slate-200 rounded-xl max-h-48 overflow-y-auto">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {selectedAttempt.student_answer?.text || selectedAttempt.student_answer}
                    </p>
                  </div>
                </div>

                {/* Mark Breakdown */}
                {selectedAttempt.evaluation?.mark_breakdown_awarded && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Mark Breakdown</p>
                    <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                      {Object.entries(selectedAttempt.evaluation.mark_breakdown_awarded).map(([key, value], i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600 capitalize">{key.replace(/_/g, " ")}</span>
                          <span className="text-sm font-semibold text-[#2E3A59]">
                            {typeof value === "object" ? `${value.awarded}/${value.max}` : value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Feedback — handles personalised_feedback (new) and feedback (old) */}
                <FeedbackPanel attempt={selectedAttempt} />

                {/* Points Covered */}
                {selectedAttempt.evaluation?.points_covered?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Points Covered</p>
                    <div className="space-y-1">
                      {selectedAttempt.evaluation.points_covered.map((pt, i) => (
                        <div key={i} className="flex items-start gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg">
                          <span className="text-emerald-600 mt-0.5 flex-shrink-0">✓</span>
                          <span className="text-xs text-emerald-800">{pt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Points Missed */}
                {selectedAttempt.evaluation?.points_missed?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Missing Points</p>
                    <div className="space-y-1">
                      {selectedAttempt.evaluation.points_missed.map((pt, i) => (
                        <div key={i} className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg">
                          <span className="text-red-500 mt-0.5 flex-shrink-0">✗</span>
                          <span className="text-xs text-red-800">{pt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reason Codes */}
                {(selectedAttempt.evaluation?.reason_codes_triggered || selectedAttempt.evaluation?.reason_codes || []).length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Areas to Improve</p>
                    <div className="space-y-2">
                      {(selectedAttempt.evaluation?.reason_codes_triggered || selectedAttempt.evaluation?.reason_codes || []).map((rc, i) => (
                        <div key={i} className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                          <span className="text-xs font-mono font-medium text-amber-700">
                            {rc.code || rc}
                          </span>
                          {rc.detail && (
                            <p className="text-xs text-amber-600 mt-1">{rc.detail}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttemptsPage;